import React, { useCallback, useContext, useMemo } from 'react';
import styled from 'styled-components';
import { Container, Drawers, flexColumn, Table as BaseTable, Header2, Body } from '@ubeya/shared-web/components';
import ConfigContext from '@ubeya/shared-web/contexts/ConfigContext';
import { FieldTypes } from '@ubeya/shared/hooks/account/useFields';
import { ReactComponent as NotCompliantIcon } from '@ubeya/shared-web/assets/compliance-no.svg';
import { ReactComponent as MaybeCompliantIcon } from '@ubeya/shared-web/assets/compliance-maybe.svg';
import { ReactComponent as CompliantIcon } from '@ubeya/shared-web/assets/confirmed.svg';
import moment from 'moment';
import FieldValueRenderer from '../../../../compliances/components/CellRenderers/FieldValueRenderer';
import { COMPLIANCE_STATUS_BY_TYPE } from 'compliances/constants';

const PositionDetails = styled(Header2)``;

const Wrapper = styled(Container)`
  ${flexColumn};
  flex: 1;
  margin: 0 0 10px 0;
`;

const Title = styled.div`
  display: flex;
  align-items: center;
  position: relative;
  width: 100%;
  gap: 8px;

  .compliance-status-wrapper {
    width: unset;
  }

  .partial-wrapper {
    width: unset;
    position: unset;
  }
`;

const Table = styled(BaseTable)`
  .row {
    &:hover {
      background-color: ${({ theme }) => theme.colors.white};
    }
  }
`;

const statusData = {
  [COMPLIANCE_STATUS_BY_TYPE.COMPLIANT]: {
    icon: <CompliantIcon />,
    slug: 'fullyCompliant'
  },
  [COMPLIANCE_STATUS_BY_TYPE.PARTIAL]: {
    icon: <MaybeCompliantIcon />,
    slug: 'partiallyCompliant'
  },
  [COMPLIANCE_STATUS_BY_TYPE.NOT_COMPLIANT]: {
    icon: <NotCompliantIcon />,
    slug: 'notCompliant'
  }
};

const EmployeeCompliancePositionSection = ({
  compliance: { minExpirationDate, position, status: complianceStatusPerEmployee, mappedComplianceFieldsEmployees },
  mappedFields,
  mappedEmployeeFields,
  setSelectedField,
  isLoading,
  employee
}) => {
  const { t, dateFormat } = useContext(ConfigContext);

  const positionFields = useMemo(() => position?.compliance?.fieldsIds || [], [position?.compliance?.fieldsIds]);

  const handleSelectField = useCallback((data) => setSelectedField({ ...data, positionId: position.id }), [
    position.id,
    setSelectedField
  ]);

  const columns = useMemo(
    () => [
      {
        Header: t('fieldName'),
        dataKey: 'fieldName',
        formatter: (value) => value
      },

      {
        Header: t('fieldValue'),
        Cell: ({ rowData }) => {
          let value = mappedEmployeeFields[`field-${rowData.fieldData?.id}`];
          if (rowData.fieldData.fieldTypeId === FieldTypes.FIELD_TYPE_OPTIONS) {
            value = (Array.isArray(value) ? value : [value]).map((fieldOptionId) => {
              const fieldOption = rowData.fieldData.options.find((option) => option.id === fieldOptionId);
              return {
                id: fieldOption?.id,
                value: fieldOption?.title
              };
            });
          }
          return (
            <FieldValueRenderer
              rowData={rowData}
              employeeId={employee.id}
              firstName={employee.firstName}
              lastName={employee.lastName}
              fieldData={rowData.fieldData}
              value={value}
              setSelectedField={handleSelectField}
            />
          );
        },

        cellRendererProps: { style: { height: '100%', width: '100%' } },
        cellWrapperProps: { style: { padding: '2px' } }
      }
    ],
    [employee.firstName, employee.id, employee.lastName, handleSelectField, mappedEmployeeFields, t]
  );

  const { icon = null, slug = '' } = statusData?.[complianceStatusPerEmployee?.status] || {};

  const data = useMemo(
    () =>
      (positionFields || []).map((id) => ({
        fieldName: mappedFields[id]?.name || '',
        fieldData: mappedComplianceFieldsEmployees[id]
      })),
    [mappedComplianceFieldsEmployees, mappedFields, positionFields]
  );

  return (
    <Drawers.Section
      title={
        <Title>
          {icon}
          <PositionDetails>{position?.label}</PositionDetails>
          <Body>
            {t(slug)}
            {minExpirationDate ? ` (${t('until')} ${moment(minExpirationDate).format(dateFormat)})` : ''}
          </Body>
        </Title>
      }>
      <Wrapper>
        <Table isLoading={isLoading} columns={columns} data={data} noCellPadding autoHeight headerRowHeight={50} />
      </Wrapper>
    </Drawers.Section>
  );
};

export default EmployeeCompliancePositionSection;
